// groups.mjs — the ONE default-project-group resolver (SB-042). Three pages used
// to inline `data?.groups?.[0]` with three different fallback behaviors; the
// picker itself is now shared, while the FALLBACK POSTURE deliberately stays a
// per-surface decision (it differs by what the surface needs):
//   * DocBrowser  — 第一百零三批起**等组上下文**（loading，不发请求）。此前它
//     回落到硬编码的 MirrorMii slug 就开始请求树：只属新组的成员会先看到一个
//     404 面板、等 /api/me 回来才自愈——那个 404 说的不是真的。
//   * SubmitTicket — needs the real integer id → must surface an error;
//   * WorkHours   — the picker is optional → degrades to an empty backlog list.
export function firstGroup(resp) {
  const g = resp?.groups?.[0];
  return g && (g.slug || g.id != null) ? g : null;
}

// 播种的默认组 slug——只作 pickCurrent 的回落**排序**用（多组成员没存过选择时
// 先落到默认组）。第一百零三批起不再导出：任何页面都不该拿一个硬编码 slug 去
// 发请求，组上下文未就绪的正确形态是 loading，不是猜一个组。
const DEFAULT_GROUP_SLUG = 'MirrorMii';

// ── 第一百零一批（多项目制）：当前组的选取与持久 ────────────────────────────
// 回落次序：localStorage 里存的组仍在我的列表里 → 用它；否则默认组（MirrorMii）
// 若在列表里 → 用它；否则列表第一项。**存的组消失**（被移出成员、组归档）时
// 静默回落而不是报错——那不是用户做错了什么。
export const GROUP_STORE_KEY = 'dc.currentGroupId';

export function pickCurrent(groups, storedId) {
  const list = Array.isArray(groups) ? groups : [];
  if (list.length === 0) return null;
  if (storedId != null) {
    const hit = list.find((g) => String(g.id) === String(storedId));
    if (hit) return hit;
  }
  return list.find((g) => g.slug === DEFAULT_GROUP_SLUG) ?? list[0];
}

// ── 第一百零二批：组名的**唯一**取名规则 ────────────────────────────────────
// 「按界面语言取 displayName/displayNameEn，回落 slug」这句话本批要在四个地方成立
// （工具条常驻 Chip、抽屉头部、账号菜单的切换项、管理页的组编辑器），而它已经在
// AppShell 的切换项里被就地写过一遍。第五十六批的教训是同一条规则一旦有两份手写
// 实现就会漂——所以趁着要用第二次，先把它收成一个纯函数。
//
// 回落链是三段而不是两段：本语言名 → **另一语言名** → slug。理由是 slug 是发布
// 目录的一段（`distill-mii` 这种），它出现在界面上只该意味着"这个组两个名字都没
// 填"；而"只填了中文名"的组（建组表单强制双名之前建的）掉到 slug 是把一个能读的
// 名字换成一个不能读的标识符。
export function groupLabel(group, lang) {
  if (!group || typeof group !== 'object') return '';
  const zh = typeof group.displayName === 'string' ? group.displayName.trim() : '';
  const en = typeof group.displayNameEn === 'string' ? group.displayNameEn.trim() : '';
  const slug = typeof group.slug === 'string' ? group.slug.trim() : '';
  const [first, second] = lang === 'en' ? [en, zh] : [zh, en];
  return first || second || slug || '';
}

// 一串组 id → 可读的组名（管理页账号表的"项目组"列）。
// 认不出的 id **不静默丢弃**，而是显示 `#<id>`：账号挂在一个已归档（或已消失）的组
// 上是真实存在的状态——关系表的行不会因为组归档就自己消失。把它从这一列里抹掉，
// 管理员看到的就是"这个账号只在剩下那几个组里"，而组编辑是**替换语义**：照着那个
// 错误的印象按一次保存，就真的把它抹掉了。
export function labelGroupIds(ids, groups, lang) {
  const list = Array.isArray(ids) ? ids : [];
  const by = new Map();
  for (const g of Array.isArray(groups) ? groups : []) {
    if (g && g.id != null) by.set(String(g.id), g);
  }
  return list.map((id) => {
    const g = by.get(String(id));
    return (g && groupLabel(g, lang)) || `#${id}`;
  });
}

// localStorage 包一层：隐私模式/SSR 下不可用时静默退化为"不记忆"。
export function readStoredGroupId(storage) {
  try {
    return (storage ?? window.localStorage).getItem(GROUP_STORE_KEY);
  } catch { return null; }
}

export function storeGroupId(id, storage) {
  try {
    (storage ?? window.localStorage).setItem(GROUP_STORE_KEY, String(id));
  } catch { /* 不记忆即可，别打断切换 */ }
}
